import {
    AssetType,
    FilterConfig,
    FolderTreeResponse,
    SortConfig,
    SortField,
    SortOrder,
    ViewMode,
} from './AssetExplorer.types';

export const ASSET_EXPLORER_UI_STATE_STORAGE_KEY = 'asset-explorer-ui-state';

const SORT_FIELDS = new Set<SortField>(['name', 'createdAt', 'updatedAt', 'size', 'type']);
const SORT_ORDERS = new Set<SortOrder>(['asc', 'desc']);
const VIEW_MODES = new Set<ViewMode>(['grid', 'table']);
const FILTER_TYPES = new Set<AssetType | 'ALL'>([
    'ALL',
    'IMAGE',
    'VIDEO',
    'AUDIO',
    'ZIP',
    'DOCUMENT',
    'OTHER',
]);

export interface PersistedAssetExplorerUiState {
    viewMode?: ViewMode;
    sortConfig?: SortConfig;
    filterConfig?: FilterConfig;
    selectedFolderId?: string;
}

/**
 * Reads persisted toolbar / navigation state.
 * `storageKey` lets each explorer mount (Asset Management vs Beam on Screen) keep its own state.
 */
export function readPersistedAssetExplorerUiState(
    storageKey: string = ASSET_EXPLORER_UI_STATE_STORAGE_KEY,
): PersistedAssetExplorerUiState {
    if (typeof window === 'undefined') {
        return {};
    }
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const result: PersistedAssetExplorerUiState = {};

        if (typeof parsed.viewMode === 'string' && VIEW_MODES.has(parsed.viewMode as ViewMode)) {
            result.viewMode = parsed.viewMode as ViewMode;
        }

        if (parsed.sortConfig && typeof parsed.sortConfig === 'object') {
            const sc = parsed.sortConfig as Record<string, unknown>;
            const field = sc.field;
            const order = sc.order;
            if (
                typeof field === 'string' &&
                SORT_FIELDS.has(field as SortField) &&
                typeof order === 'string' &&
                SORT_ORDERS.has(order as SortOrder)
            ) {
                result.sortConfig = { field: field as SortField, order: order as SortOrder };
            }
        }

        if (parsed.filterConfig && typeof parsed.filterConfig === 'object') {
            const fc = parsed.filterConfig as Record<string, unknown>;
            const type = fc.type;
            if (typeof type === 'string' && FILTER_TYPES.has(type as AssetType | 'ALL')) {
                result.filterConfig =
                    type === 'ALL' ? {} : { type: type as AssetType };
            }
        }

        if (typeof parsed.selectedFolderId === 'string' && parsed.selectedFolderId.trim()) {
            result.selectedFolderId = parsed.selectedFolderId.trim();
        }

        return result;
    } catch {
        return {};
    }
}

export function writePersistedAssetExplorerUiState(
    state: PersistedAssetExplorerUiState,
    storageKey: string = ASSET_EXPLORER_UI_STATE_STORAGE_KEY,
): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
        // ignore quota / private mode
    }
}

/** Breadcrumb chain from tree root to the folder with `targetId`. */
export function buildFolderBreadcrumbPath(
    nodes: FolderTreeResponse[],
    targetId: string,
): FolderTreeResponse[] {
    const path: FolderTreeResponse[] = [];
    const walk = (folders: FolderTreeResponse[], id: string): boolean => {
        for (const f of folders) {
            if (f._id === id) {
                path.push(f);
                return true;
            }
            if (f.children && f.children.length > 0 && walk(f.children, id)) {
                path.unshift(f);
                return true;
            }
        }
        return false;
    };
    walk(nodes, targetId);
    return path;
}
